import React, { useMemo } from 'react';
import { hintClass, nestedPanelClass, sectionLabelClass } from './challengeWireFormStyles';

function accountSummary(account) {
	return account.profile_names?.length ? account.profile_names.join(', ') : 'No profiles';
}

function AudienceAccountRow({ account, checked, disabled, onToggle }) {
	const checkboxId = `audience-account-${account.user_id}`;

	return (
		<li>
			<label
				htmlFor={checkboxId}
				className={`flex items-start gap-3 rounded-lg px-2 py-2 transition ${
					disabled
						? 'cursor-default opacity-80'
						: 'cursor-pointer hover:bg-white dark:hover:bg-dark-card'
				}`}
			>
				<input
					id={checkboxId}
					type="checkbox"
					checked={checked}
					disabled={disabled}
					onChange={(event) => onToggle(account.user_id, event.target.checked)}
					className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-dark-bg dark:focus:ring-blue-400"
				/>
				<span className="min-w-0 flex-1">
					<span className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-dark-text">
						{account.account_name}
						{account.is_creator && (
							<span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:bg-opacity-40 dark:text-blue-300">
								You
							</span>
						)}
					</span>
					<p className={`mt-0.5 ${hintClass}`}>{accountSummary(account)}</p>
				</span>
			</label>
		</li>
	);
}

export default function CommunityAudiencePicker({
	accounts = [],
	audienceMode,
	invitedUserIds = [],
	onAudienceModeChange,
	onInvitedUserIdsChange
}) {
	const invitedSet = useMemo(() => new Set(invitedUserIds), [invitedUserIds]);

	const selectedAccounts = useMemo(() => {
		return accounts.filter((account) => invitedSet.has(account.user_id));
	}, [accounts, invitedSet]);

	const selectedProfileCount = selectedAccounts.reduce(
		(total, account) => total + (account.profile_count || account.profiles?.length || 0),
		0
	);

	function handleModeChange(mode) {
		onAudienceModeChange(mode);
		if (mode === 'full_network') {
			onInvitedUserIdsChange(accounts.map((account) => account.user_id));
		}
	}

	function handleAccountToggle(userId, checked) {
		const creatorAccount = accounts.find((account) => account.is_creator);
		const creatorId = creatorAccount?.user_id;
		const next = new Set(invitedUserIds);

		if (checked) {
			next.add(userId);
		} else if (userId !== creatorId) {
			next.delete(userId);
		}

		if (creatorId) next.add(creatorId);
		onInvitedUserIdsChange(Array.from(next));
		onAudienceModeChange('custom');
	}

	if (!accounts.length) return null;

	return (
		<div className={nestedPanelClass}>
			<p className={sectionLabelClass}>Audience</p>

			<div className="flex flex-wrap gap-2">
				<button
					type="button"
					onClick={() => handleModeChange('full_network')}
					className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
						audienceMode === 'full_network'
							? 'border-blue-600 bg-blue-600 text-white'
							: 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300'
					}`}
				>
					Everyone in network
				</button>
				<button
					type="button"
					onClick={() => handleModeChange('custom')}
					className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
						audienceMode === 'custom'
							? 'border-blue-600 bg-blue-600 text-white'
							: 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300'
					}`}
				>
					Choose accounts
				</button>
			</div>

			{audienceMode === 'custom' && (
				<ul className="mt-3 max-h-56 space-y-1 overflow-y-auto overscroll-contain pr-0.5">
					{accounts.map((account) => (
						<AudienceAccountRow
							key={account.user_id}
							account={account}
							checked={invitedSet.has(account.user_id)}
							disabled={account.is_creator}
							onToggle={handleAccountToggle}
						/>
					))}
				</ul>
			)}

			<p className={`mt-3 ${hintClass}`}>
				{audienceMode === 'full_network' ? (
					<>
						Visible to <strong>{accounts.reduce((n, a) => n + (a.profile_count || 0), 0)}</strong>{' '}
						profiles across <strong>{accounts.length}</strong> accounts.
					</>
				) : (
					<>
						Inviting <strong>{selectedProfileCount}</strong> profiles across{' '}
						<strong>{selectedAccounts.length}</strong> accounts.
					</>
				)}
			</p>
		</div>
	);
}
